import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Checkbox,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
  Menu,
  MenuItem as MenuItemComponent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { employeeAPI } from '../services/api';
import { downloadQrCode } from '../utils/urlUtils';
import EmployeeForm from '../components/EmployeeForm';
import QRCodeDialog from '../components/QRCodeDialog';
import ExcelImport from '../components/ExcelImport';
import '../styles/AppShell.css';

const Dashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [openQR, setOpenQR] = useState(false);
  const [openExcel, setOpenExcel] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [downloadingQRs, setDownloadingQRs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedEmployeeForMenu, setSelectedEmployeeForMenu] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    navigate('/login');
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeAPI.getAll();
      setEmployees(response.data.employees);
    } catch (err) {
      setError('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const withQR = getFilteredEmployeesByTab().filter((emp) => emp.qr_code_url);
      setSelectedEmployees(withQR.map((emp) => emp.id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleBulkDownloadQR = async () => {
    if (selectedEmployees.length === 0) {
      alert('Please select at least one employee to download QR codes');
      return;
    }
    const employeesToDownload = employees.filter(
      (emp) => selectedEmployees.includes(emp.id) && emp.qr_code_url
    );
    if (employeesToDownload.length === 0) {
      alert('No QR codes available for selected employees');
      return;
    }
    setDownloadingQRs(true);
    try {
      let successCount = 0;
      let failCount = 0;
      for (const employee of employeesToDownload) {
        try {
          const success = await downloadQrCode(
            employee.qr_code_url,
            employee.employee_code
          );
          if (success) successCount++;
          else failCount++;
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (e) {
          failCount++;
        }
      }
      if (failCount === 0) {
        alert(`Successfully downloaded ${successCount} QR codes!`);
      } else {
        alert(`Downloaded ${successCount} QR codes\nFailed: ${failCount}`);
      }
      setSelectedEmployees([]);
    } catch (e) {
      alert('Failed to download QR codes. Please try again.');
    } finally {
      setDownloadingQRs(false);
    }
  };

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setOpenForm(true);
  };
  const handleExcelImport = () => setOpenExcel(true);
  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setOpenForm(true);
  };
  const handleViewQR = (employee) => {
    setSelectedEmployee(employee);
    setOpenQR(true);
  };
  const handleGenerateQR = async (employee) => {
    try {
      await employeeAPI.generateQR(employee.id);
      fetchEmployees();
      alert('QR Code regenerated successfully!');
    } catch (e) {
      setError('Failed to regenerate QR code');
    }
  };
  const handleViewProfile = (employee) => {
    window.open(`/card/${employee.employee_code}`, '_blank');
  };
  const handleActionMenuOpen = (event, employee) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedEmployeeForMenu(employee);
  };
  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedEmployeeForMenu(null);
  };

  const filteredEmployees = employees.filter((employee) => {
    const matchesSearch =
      searchTerm === '' ||
      employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.employee_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.phone?.includes(searchTerm);
    const matchesDepartment =
      departmentFilter === '' || employee.department === departmentFilter;
    const matchesDesignation =
      designationFilter === '' || employee.designation === designationFilter;
    const matchesStatus =
      statusFilter === '' ||
      (statusFilter === 'active' && employee.is_active) ||
      (statusFilter === 'inactive' && !employee.is_active);
    return matchesSearch && matchesDepartment && matchesDesignation && matchesStatus;
  });

  const getFilteredEmployeesByTab = () => {
    switch (activeTab) {
      case 1:
        return filteredEmployees.filter((emp) => emp.is_active);
      case 2:
        return filteredEmployees.filter((emp) => !emp.is_active);
      case 3:
        return filteredEmployees.filter((emp) => emp.qr_code_url);
      case 4:
        return filteredEmployees.filter((emp) => !emp.qr_code_url);
      default:
        return filteredEmployees;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('');
    setDesignationFilter('');
    setStatusFilter('');
  };

  const stats = {
    total: employees.length,
    active: employees.filter((emp) => emp.is_active).length,
    inactive: employees.filter((emp) => !emp.is_active).length,
    withQR: employees.filter((emp) => emp.qr_code_url).length,
    withoutQR: employees.filter((emp) => !emp.qr_code_url).length,
  };

  const departments = [
    ...new Set(employees.map((emp) => emp.department).filter(Boolean)),
  ];
  const designations = [
    ...new Set(employees.map((emp) => emp.designation).filter(Boolean)),
  ];

  const handleDeleteEmployee = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await employeeAPI.delete(id);
        fetchEmployees();
      } catch (e) {
        setError('Failed to delete employee');
      }
    }
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setEditingEmployee(null);
  };
  const handleFormSubmit = async () => {
    await fetchEmployees();
    handleFormClose();
  };

  const employeesWithQR = getFilteredEmployeesByTab().filter(
    (emp) => emp.qr_code_url
  );
  const isAllSelected =
    employeesWithQR.length > 0 &&
    selectedEmployees.length === employeesWithQR.length;
  const isIndeterminate =
    selectedEmployees.length > 0 &&
    selectedEmployees.length < employeesWithQR.length;

  const tabEmployees = getFilteredEmployeesByTab();

  const metrics = [
    { label: 'Total', value: stats.total, icon: <PeopleIcon />, cls: '' },
    { label: 'Active', value: stats.active, icon: <WorkIcon />, cls: 'metric-green' },
    { label: 'Inactive', value: stats.inactive, icon: <PeopleIcon />, cls: 'metric-amber' },
    { label: 'With QR', value: stats.withQR, icon: <QrCodeIcon />, cls: 'metric-blue' },
    { label: 'Without QR', value: stats.withoutQR, icon: <QrCodeIcon />, cls: 'metric-rose' },
  ];

  if (loading) {
    return (
      <div className="app-shell">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {isMobile && (
        <AppBar
          position="static"
          sx={{
            background: 'linear-gradient(135deg, #0B1D4A 0%, #163A7A 100%)',
            mb: 0,
          }}
        >
          <Toolbar>
            <Box display="flex" alignItems="center" gap={1.2} flex={1}>
              <img
                src="/images/logo.png"
                alt="Logo"
                style={{ width: 32, height: 32, borderRadius: 8, background: '#fff', padding: 2 }}
              />
              <Typography variant="h6" noWrap sx={{ fontWeight: 750 }}>
                Star Security
              </Typography>
            </Box>
            <IconButton color="inherit" onClick={() => setMobileMenuOpen(true)}>
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box sx={{ width: 260, p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 750, mb: 1 }}>
            Menu
          </Typography>
          <List>
            <ListItem button onClick={() => { handleAddEmployee(); setMobileMenuOpen(false); }}>
              <ListItemIcon><AddIcon /></ListItemIcon>
              <ListItemText primary="Add Employee" />
            </ListItem>
            <ListItem button onClick={() => { handleExcelImport(); setMobileMenuOpen(false); }}>
              <ListItemIcon><UploadIcon /></ListItemIcon>
              <ListItemText primary="Import Excel" />
            </ListItem>
            <Divider sx={{ my: 1 }} />
            <ListItem button onClick={handleLogout}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <Container maxWidth="xl" sx={{ mt: isMobile ? 2 : 3.5, mb: 5, px: isMobile ? 1.5 : 3 }}>
        {!isMobile && (
          <Box className="brand-bar" sx={{ mb: 3.5 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <img src="/images/logo.png" alt="Star Security" className="brand-bar-logo" />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                  Star Security Services
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.82, fontWeight: 500, mt: 0.3 }}>
                  Employee identity & QR management
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.4)',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Logout
            </Button>
          </Box>
        )}

        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobile ? 'stretch' : 'flex-end'}
          mb={3}
          gap={2}
        >
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stats.total} employees · issue cards, import, and manage access
            </Typography>
          </Box>
          {!isMobile && (
            <Box display="flex" gap={1.5}>
              <Button variant="outlined" startIcon={<UploadIcon />} onClick={handleExcelImport}>
                Import Excel
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddEmployee}>
                Add Employee
              </Button>
            </Box>
          )}
        </Box>

        <Grid container spacing={isMobile ? 1.5 : 2} mb={3}>
          {metrics.map((m) => (
            <Grid item xs={6} sm={4} md={2.4} key={m.label}>
              <Card className={`metric-tile ${m.cls}`} elevation={0}>
                <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                  <div className="metric-icon">{m.icon}</div>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    {m.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650, letterSpacing: '0.04em' }}>
                    {m.label.toUpperCase()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {selectedEmployees.length > 0 && (
          <Paper
            elevation={0}
            className="section-panel"
            sx={{
              p: 2,
              mb: 2.5,
              background: 'linear-gradient(135deg, rgba(11,29,74,0.04), rgba(124,179,66,0.08))',
            }}
          >
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'stretch' : 'center'}
              justifyContent="space-between"
              gap={2}
            >
              <Typography sx={{ fontWeight: 700 }}>
                {selectedEmployees.length} selected for QR download
              </Typography>
              <Box display="flex" gap={1.5} flexDirection={isMobile ? 'column' : 'row'}>
                <Button
                  variant="contained"
                  startIcon={downloadingQRs ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                  onClick={handleBulkDownloadQR}
                  disabled={downloadingQRs}
                  fullWidth={isMobile}
                >
                  {downloadingQRs ? 'Downloading…' : `Download ${selectedEmployees.length} QR`}
                </Button>
                <Button variant="outlined" onClick={() => setSelectedEmployees([])} fullWidth={isMobile}>
                  Clear
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        <Paper elevation={0} className="section-panel" sx={{ p: isMobile ? 2 : 2.5, mb: 2.5 }}>
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'stretch' : 'center'}
            justifyContent="space-between"
            mb={2}
            gap={1.5}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 750 }}>
              Search & filters
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                size="small"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? 'contained' : 'outlined'}
              >
                Filters
              </Button>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                variant="outlined"
                disabled={!searchTerm && !departmentFilter && !designationFilter && !statusFilter}
              >
                Clear
              </Button>
            </Box>
          </Box>

          <TextField
            fullWidth
            placeholder="Search name, ID, email, or phone…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: showFilters ? 2 : 0 }}
          />

          {showFilters && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    label="Department"
                  >
                    <MenuItem value="">All Departments</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Designation</InputLabel>
                  <Select
                    value={designationFilter}
                    onChange={(e) => setDesignationFilter(e.target.value)}
                    label="Designation"
                  >
                    <MenuItem value="">All Designations</MenuItem>
                    {designations.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" height="100%">
                  <Typography variant="body2" color="text.secondary">
                    Showing {tabEmployees.length} of {employees.length}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>

        <Paper elevation={0} className="section-panel" sx={{ mb: 2.5 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1 }}
          >
            <Tab label={<Badge badgeContent={filteredEmployees.length} color="primary" sx={{ '& .MuiBadge-badge': { right: -12 } }}>All</Badge>} />
            <Tab label={<Badge badgeContent={filteredEmployees.filter((e) => e.is_active).length} color="success" sx={{ '& .MuiBadge-badge': { right: -12 } }}>Active</Badge>} />
            <Tab label={<Badge badgeContent={filteredEmployees.filter((e) => !e.is_active).length} color="warning" sx={{ '& .MuiBadge-badge': { right: -12 } }}>Inactive</Badge>} />
            <Tab label={<Badge badgeContent={filteredEmployees.filter((e) => e.qr_code_url).length} color="info" sx={{ '& .MuiBadge-badge': { right: -12 } }}>With QR</Badge>} />
            <Tab label={<Badge badgeContent={filteredEmployees.filter((e) => !e.qr_code_url).length} color="error" sx={{ '& .MuiBadge-badge': { right: -12 } }}>No QR</Badge>} />
          </Tabs>
        </Paper>

        {isMobile ? (
          <Box>
            {tabEmployees.map((employee) => (
              <Card key={employee.id} elevation={0} className="section-panel" sx={{ mb: 1.5, p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Checkbox
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={() => handleSelectEmployee(employee.id)}
                      disabled={!employee.qr_code_url}
                    />
                    <Avatar src={employee.photo_url || undefined} sx={{ width: 44, height: 44 }}>
                      {employee.first_name?.charAt(0)}
                      {employee.last_name?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 750, lineHeight: 1.2 }}>
                        {employee.first_name} {employee.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {employee.employee_code}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={(e) => handleActionMenuOpen(e, employee)}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>
                <Box display="flex" flexDirection="column" gap={0.8}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                    <Typography variant="body2">{employee.email}</Typography>
                  </Box>
                  {employee.phone && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                      <Typography variant="body2">{employee.phone}</Typography>
                    </Box>
                  )}
                  <Box display="flex" gap={0.8} flexWrap="wrap" mt={0.5}>
                    <Chip label={employee.department || 'N/A'} size="small" variant="outlined" />
                    <Chip
                      label={employee.is_active ? 'Active' : 'Inactive'}
                      color={employee.is_active ? 'success' : 'error'}
                      size="small"
                    />
                    <Chip
                      label={employee.qr_code_url ? 'QR Ready' : 'No QR'}
                      color={employee.qr_code_url ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        ) : (
          <Paper elevation={0} className="section-panel" sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        disabled={employeesWithQR.length === 0}
                      />
                    </TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Designation</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>QR</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tabEmployees.map((employee) => (
                    <TableRow key={employee.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedEmployees.includes(employee.id)}
                          onChange={() => handleSelectEmployee(employee.id)}
                          disabled={!employee.qr_code_url}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar src={employee.photo_url || undefined} sx={{ width: 40, height: 40 }}>
                            {employee.first_name?.charAt(0)}
                            {employee.last_name?.charAt(0)}
                          </Avatar>
                          <Typography sx={{ fontWeight: 700 }}>
                            {employee.first_name} {employee.last_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {employee.employee_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">{employee.email}</Typography>
                        {employee.phone && (
                          <Typography variant="caption" color="text.secondary">{employee.phone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={employee.department || 'N/A'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employee.designation || employee.position || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={employee.is_active ? 'Active' : 'Inactive'}
                          color={employee.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={employee.qr_code_url ? 'Ready' : 'Missing'}
                          color={employee.qr_code_url ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="inline-flex" gap={0.25}>
                          <Tooltip title="View QR">
                            <IconButton size="small" onClick={() => handleViewQR(employee)} color="primary">
                              <QrCodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Regenerate QR">
                            <IconButton size="small" onClick={() => handleGenerateQR(employee)}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditEmployee(employee)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View card">
                            <IconButton size="small" onClick={() => handleViewProfile(employee)} color="secondary">
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteEmployee(employee.id)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {tabEmployees.length === 0 && (
          <Box textAlign="center" py={8}>
            <PeopleIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1.5, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No employees found
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {searchTerm || departmentFilter || designationFilter || statusFilter
                ? 'Try adjusting your search or filters'
                : 'Add your first employee to get started'}
            </Typography>
            {!searchTerm && !departmentFilter && !designationFilter && !statusFilter && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddEmployee}>
                Add Employee
              </Button>
            )}
          </Box>
        )}

        <Dialog
          open={openForm}
          onClose={handleFormClose}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <EmployeeForm
              employee={editingEmployee}
              onSubmit={handleFormSubmit}
              onCancel={handleFormClose}
            />
          </DialogContent>
        </Dialog>

        <QRCodeDialog open={openQR} employee={selectedEmployee} onClose={() => setOpenQR(false)} />
        <ExcelImport open={openExcel} onClose={() => setOpenExcel(false)} onSuccess={fetchEmployees} />

        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
        >
          <MenuItemComponent onClick={() => { handleViewQR(selectedEmployeeForMenu); handleActionMenuClose(); }}>
            <ListItemIcon><QrCodeIcon fontSize="small" /></ListItemIcon>
            View QR Code
          </MenuItemComponent>
          <MenuItemComponent onClick={() => { handleGenerateQR(selectedEmployeeForMenu); handleActionMenuClose(); }}>
            <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
            Regenerate QR
          </MenuItemComponent>
          <MenuItemComponent onClick={() => { handleEditEmployee(selectedEmployeeForMenu); handleActionMenuClose(); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            Edit Employee
          </MenuItemComponent>
          <MenuItemComponent onClick={() => { handleViewProfile(selectedEmployeeForMenu); handleActionMenuClose(); }}>
            <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
            View Card
          </MenuItemComponent>
          <MenuItemComponent onClick={() => { handleDeleteEmployee(selectedEmployeeForMenu.id); handleActionMenuClose(); }}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            Delete Employee
          </MenuItemComponent>
        </Menu>

        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Star Security Services
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Powered by EnvisionTech Solutions
          </Typography>
        </Box>
      </Container>
    </div>
  );
};

export default Dashboard;
